import { useState, useEffect } from 'react';
import axios from 'axios';
import { CarFront, DollarSign, Clock, MessageSquare, TrendingUp } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import LiveVisitors from '../../components/admin/LiveVisitors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState({
    total: 0,
    available: 0,
    sold: 0,
    pending: 0,
    makes: [],
    body_types: [],
  });
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveVisitors, setLiveVisitors] = useState({ total: 0, pages: {} });

  useEffect(() => {
    fetchData();
    fetchLiveVisitors();
    
    const interval = setInterval(fetchLiveVisitors, 10000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const fetchLiveVisitors = async () => {
    try {
      const res = await axios.get(`${API}/admin/visitors/live`, { headers: getAuthHeader() });
      setLiveVisitors(res.data);
    } catch (error) {
      // Silently fail
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/vehicles/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/inquiries`, { headers: getAuthHeader() }),
      ]);
      setStats(statsRes.data);
      setInquiries(inquiriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Vehicles',
      value: stats.total,
      icon: CarFront,
      color: 'text-blue-500',
      bg: 'bg-blue-500/10',
    },
    {
      title: 'Available',
      value: stats.available,
      icon: TrendingUp,
      color: 'text-green-500',
      bg: 'bg-green-500/10',
    },
    {
      title: 'Sold',
      value: stats.sold,
      icon: DollarSign,
      color: 'text-primary',
      bg: 'bg-primary/10',
    },
    {
      title: 'Pending',
      value: stats.pending,
      icon: Clock,
      color: 'text-yellow-500',
      bg: 'bg-yellow-500/10',
    },
  ];

  const newInquiries = inquiries.filter(i => i.status === 'new').length;

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-slate-800">Tableau de Bord</h1>
            <p className="text-slate-500 text-xs">Bienvenue dans votre panneau de gestion</p>
          </div>
          
          {/* Live Visitors Badge */}
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
            </span>
            <span className="text-green-700 font-semibold text-sm">
              {liveVisitors.total} visiteur{liveVisitors.total !== 1 ? 's' : ''} en ligne
            </span>
          </div>
        </div>

        {/* Enhanced Live Visitors Analytics */}
        <LiveVisitors />

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {statCards.map((stat) => (
            <Card key={stat.title} className="bg-white border border-slate-200">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-slate-500">{stat.title}</p>
                    <p className="text-xl font-bold text-slate-800 mt-0.5">
                      {loading ? '...' : stat.value}
                    </p>
                  </div>
                  <div className={`${stat.bg} p-2 rounded-lg`}>
                    <stat.icon className={`h-4 w-4 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Recent Inquiries */}
          <Card className="bg-white border border-slate-200">
            <CardHeader className="border-b border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-slate-800 text-sm flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-blue-600" />
                  Demandes récentes
                </CardTitle>
                {newInquiries > 0 && (
                  <span className="bg-blue-100 text-blue-700 text-[10px] px-2 py-0.5 rounded-full">
                    {newInquiries} nouveau(x)
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-4 text-slate-500 text-xs">Chargement...</div>
              ) : inquiries.length > 0 ? (
                <ul className="divide-y divide-slate-100">
                  {inquiries.slice(0, 5).map((inquiry) => (
                    <li key={inquiry.id} className="p-3 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-800 text-xs truncate">{inquiry.name}</p>
                          <p className="text-[11px] text-slate-500 truncate">{inquiry.message}</p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${
                          inquiry.status === 'new' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          {inquiry.status}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="p-4 text-center text-slate-400 text-xs">
                  Aucune demande
                </div>
              )}
            </CardContent>
          </Card>

          {/* Inventory Overview */}
          <Card className="bg-white border border-slate-200">
            <CardHeader className="border-b border-slate-200 p-3">
              <CardTitle className="text-slate-800 text-sm flex items-center gap-2">
                <CarFront className="h-4 w-4 text-blue-600" />
                Aperçu de l'inventaire
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              {loading ? (
                <div className="text-slate-500 text-xs">Chargement...</div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-[10px] text-slate-500 mb-1.5">Marques en inventaire</p>
                    <div className="flex flex-wrap gap-1.5">
                      {stats.makes.length > 0 ? (
                        stats.makes.slice(0, 8).map((make) => (
                          <span key={make} className="bg-slate-100 text-slate-700 text-[10px] px-2 py-0.5 rounded-full">
                            {make}
                          </span>
                        ))
                      ) : (
                        <span className="text-slate-400 text-xs">Aucun véhicule</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 mb-1.5">Types de carrosserie</p>
                    <div className="flex flex-wrap gap-1.5">
                      {stats.body_types.length > 0 ? (
                        stats.body_types.map((type) => (
                          <span key={type} className="bg-slate-100 text-slate-700 text-[10px] px-2 py-0.5 rounded-full capitalize">
                            {type}
                          </span>
                        ))
                      ) : (
                        <span className="text-slate-400 text-xs">Aucun véhicule</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
